import type { Dict } from "../core.js";
import type { invoiceQuota as vi } from "../vi/invoiceQuota.js";

/** Namespace `invoiceQuota` — PLAT-2026-156 · ADR-049, English. */
export const invoiceQuota: Dict<typeof vi> = {
  title: "Invoice quota",
  subtitle: "Bube's invoice issuance ledger lives in bube-einvoice; the hub only sends commands and displays. The hub being down does not affect customers issuing invoices.",
  searchPlaceholder: "Search customers by name, slug, id or tax code",
  search: "Search",
  empty: "No matching customers.",
  loadFailed: "Could not read data from bube-einvoice.",
  back: "← Customers",
  noEntity: "This customer has no issuing legal entity yet — the first write (policy or grant) will create the primary one.",
  workspaceTitle: "Invoice quota of “{name}”",
  noScopes: "This customer has no invoice provider connected.",
  dirOUTBOUND: "Outbound invoices (sales)",
  dirINBOUND: "Inbound invoices (purchases)",
  model_bube_ledger: "Bube quota ledger",
  model_provider_managed: "Provider-managed",
  model_unmanaged: "Not migrated",
  enf_disabled: "Not enforced",
  enf_preparing: "Preparing — new invoices paused",
  enf_enforced: "Enforced",
  granted: "Net granted",
  consumed: "Consumed",
  held: "Held",
  available: "Available",
  revision: "Ledger revision",
  asOf: "As of {at}",
  providerManagedNote: "Quantity is managed by {provider} — Bube keeps no balance (this is not 0).",
  unmanagedNote: "Scope not migrated to the Bube ledger — issuing as before, no quota check.",
  inboundNote: "Inbound invoices do not use issuance quota.",
  noAccount: "No ledger for this scope yet.",
  actionGrant: "Grant more",
  actionPolicy: "Policy",
  actionLedger: "View ledger",
  actionHideLedger: "Hide ledger",
  noGrantPermission: "You do not have permission to grant quota.",
  noPolicyPermission: "You do not have permission to change the policy.",
  grantTitle: "Grant quota — {provider} · {direction}",
  amount: "Quota to add",
  kind: "Basis",
  kind_purchase: "Customer purchase",
  kind_gift: "Gift",
  kind_compensation: "Compensation",
  kind_transfer: "Balance transfer",
  kind_opening: "Opening balance",
  reason: "Reason (at least 3 characters)",
  documentSource: "Document source",
  documentRef: "Document code",
  documentLine: "Document line",
  documentHint: "With a document code, the same document/line cannot be granted twice, even under a new command.",
  preview: "Available: {before} → {after}",
  commandHint: "Command {id} is kept across reloads; resubmitting reuses it so nothing is added twice.",
  confirm: "Record grant",
  confirmAgain: "Click again to confirm",
  cancel: "Cancel",
  saving: "Saving…",
  saved: "Recorded.",
  replayed: "This command was already recorded — nothing added again.",
  checkCommand: "Check command result",
  commandFound: "The command was recorded — balance updated.",
  commandMissing: "The command was not recorded; safe to resubmit.",
  reverseTitle: "Reverse / adjust a grant",
  reverseAmount: "Quota to reverse (max {max})",
  reverseConfirm: "Record reversal",
  reversible: "{n} reversible",
  actionReverse: "Reverse",
  noReversePermission: "You do not have permission to reverse grants.",
  policyTitle: "Policy — {provider} · {direction}",
  policyModel: "Allocation model",
  policyEnforcement: "Enforcement",
  policyHint: "Only the “Bube quota ledger” can be enforced. Changing the model never deletes the old ledger.",
  policyEnforceWarn: "Enforced: new invoices are blocked when quota runs out. Grant opening quota before enabling.",
  policyConfirm: "Save policy",
  colTime: "Time",
  colType: "Type",
  colDelta: "Change",
  colActor: "Actor",
  colReason: "Reason",
  colRevision: "Revision",
  type_grant: "Grant",
  type_reserve: "Hold",
  type_consume: "Consumed",
  type_release: "Released",
  type_adjust: "Reversal",
  ledgerEmpty: "No ledger entries.",
  err_INVOICE_QUOTA_DOCUMENT_DUPLICATE: "This document line was already granted.",
  err_INVOICE_QUOTA_COMMAND_CONFLICT: "This command id was used for different content — reopen the form for a new command.",
  err_INVOICE_QUOTA_REVISION_CONFLICT: "The ledger changed elsewhere — reload and try again.",
  err_INVOICE_QUOTA_NOT_BUBE_MANAGED: "This scope does not use the Bube quota ledger.",
  err_INVOICE_QUOTA_SCOPE_NOT_CONFIGURED: "The scope has no quota configuration.",
  err_INVOICE_QUOTA_DIRECTION_DISABLED: "This invoice direction does not use issuance quota.",
  err_INVOICE_QUOTA_ADJUST_EXCEEDS: "The reversal exceeds what is allowed (held or consumed quota is never recalled).",
  err_unknown: "Could not complete the action.",
  blockedInvoices: "{n} invoices waiting for quota (kept ready, they rerun automatically when quota is available)",
  fulfillTitle: "Partner fulfillment",
  fulfillHint: "Bube's entitlement is effective as soon as it is recorded. This only tracks what was done on the provider side — success or failure never changes the balance.",
  ful_unverified: "Unverified",
  ful_dashboard_done: "Granted on dashboard",
  ful_api_requested: "API sent — pending / unknown",
  ful_api_succeeded: "API succeeded",
  ful_api_failed: "API failed",
  ful_api_unsupported: "Provider/channel does not support API",
  ful_needs_reconcile: "Needs reconciliation with the provider",
  fulMarkDashboard: "Granted on dashboard",
  fulRequestApi: "Request via API",
  fulEvidence: "Reference / evidence",
  fulNote: "Note",
  fulNoPermission: "You do not have permission to record partner fulfillment.",
  fulReconcileHint: "The API request is pending but someone also handled it on the dashboard: check with the provider first — the hub never resends on its own.",
  fulDone: "Fulfillment recorded.",
  grantsHeading: "Grants",
  err_INVOICE_QUOTA_FULFILLMENT_CONFLICT: "This grant already has a partner fulfillment or needs reconciliation — nothing was sent.",
  err_INVOICE_QUOTA_CUTOVER_BLOCKED: "Not ready to be enforced yet.",
  err_INVOICE_QUOTA_CUTOVER_STATE: "This step is not valid in the current cutover state.",
  actionCutover: "Cut over",
  cutoverTitle: "Move scope to the Bube ledger — {provider}",
  cutoverIntro: "An ordered process: begin (pauses NEW invoices to drain) → opening balance (once) → enforce. Issuance history before the start mark is never counted in the ledger.",
  cutoverId: "Cutover id",
  cutoverNone: "This cutover has not started.",
  cutoverState: "State: {state}",
  cst_preparing: "Preparing",
  cst_balanced: "Opening balance recorded",
  cst_enforced: "Enforced",
  cst_rolled_back: "Rolled back",
  cutoverWatermark: "History mark: {n} invoices already issued (as of {at}) — not counted in the ledger.",
  cutoverInFlight: "In flight: {processing} processing at the provider · {ready} queued · {holds} dangling holds · {waiting} waiting",
  cutoverBegin: "Begin (pause new invoices)",
  cutoverOpening: "Record opening balance",
  cutoverOpeningAmount: "Opening balance (remaining quota per the reconciled record)",
  cutoverEnforce: "Enable enforcement",
  cutoverRollback: "Roll back (keep the ledger)",
  cutoverRollbackHint: "Rollback only turns enforcement off; grants, consumed quota and holds are kept. Invoices issued while enforcement is off are not recorded in the ledger.",
  cutoverReady: "Ready to enforce.",
  blk_state: "Not yet at the “opening balance recorded” step",
  blk_in_flight: "Invoices still processing or queued — not drained",
  blk_holds: "Dangling holds not yet consumed/released",
  blk_no_opening_balance: "No opening balance above 0 (enforcement is never enabled with 0)",
  blk_projection_mismatch: "Balance differs from the ledger total — investigate, never auto-fix",
  cutoverNeedsPolicy: "You need the policy permission.",
};
